import { AutocompleteTextField } from 'hic-components'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useCountriesQuery, CountryFragment } from 'src/api/api.generated'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'

export type CountryInputProps = {
  value:
    | CountryFragment
    | {
        isoCode: string
      }
    | {
        id: string
      }
    | null
  onChange: (value: CountryFragment | null) => void
  error?: boolean
  disabled?: boolean
  defaultValue?: string
  size?: 'small' | 'medium'
}

export default function CountryInput(props: CountryInputProps) {
  const MAIN_COUNTRY_ISO_CODE = 'IT'

  const localizeString = useLocalizeString()
  const { value, onChange, error, disabled, defaultValue, size } = props
  const { data } = useCountriesQuery({ fetchPolicy: 'cache-first' })

  const options = useMemo(() => {
    if (data?.countries) {
      const { mainCountry, otherCountries } = data.countries.reduce(
        (prev, current) => {
          if (!prev.mainCountry) {
            if (current.isoCode === MAIN_COUNTRY_ISO_CODE) return { ...prev, mainCountry: current }
          }
          return { ...prev, otherCountries: [...prev.otherCountries, current] }
        },
        { mainCountry: undefined as CountryFragment | undefined, otherCountries: [] as CountryFragment[] },
      )
      return [
        ...(mainCountry ? [mainCountry] : []),
        ...(otherCountries.sort((a, b) => {
          if (localizeString(a.name) < localizeString(b.name)) return -1
          if (localizeString(a.name) > localizeString(b.name)) return 1
          return 0
        }) || []),
      ]
    }
    return []
  }, [data])

  const [localValue, setLocalValue] = useState<CountryFragment | null>(null)
  const onLocalChange = useCallback(
    (newLocalValue: CountryFragment | null) => {
      if (JSON.stringify(newLocalValue) !== JSON.stringify(localValue)) {
        setLocalValue(newLocalValue)
      }
      if (areCountriesDifferent(newLocalValue, value)) {
        onChange?.(newLocalValue)
      }
    },
    [localValue, value, onChange],
  )

  useEffect(() => {
    if (data) {
      if (value === null && localValue !== null) {
        onLocalChange(null)
      }
      if (value !== null) {
        if ('id' in value) {
          if (value.id !== localValue?.id) {
            const newLocalValue = data.countries?.find((c) => c.id === value.id) || null
            onLocalChange(newLocalValue)
          }
        } else if ('isoCode' in value) {
          if (value.isoCode !== localValue?.isoCode) {
            const newLocalValue = data.countries?.find((c) => c.isoCode === value.isoCode) || null
            onLocalChange(newLocalValue)
          }
        }
      } else if (defaultValue) {
        const firstCountry = data.countries?.find((c) => c.isoCode === defaultValue)
        firstCountry ? onLocalChange(firstCountry) : onLocalChange(null)
      }
    }
  }, [value, data])

  return (
    <AutocompleteTextField<CountryFragment> //
      getOptionLabel={(option) => `${localizeString(option.name)} (${option.isoCode})`}
      disabled={disabled}
      options={options}
      value={localValue}
      onChange={onLocalChange}
      error={error}
      size={size}
    />
  )
}

function areCountriesDifferent(
  country1:
    | CountryFragment
    | {
        isoCode: string
      }
    | {
        id: string
      }
    | null,
  country2:
    | CountryFragment
    | {
        isoCode: string
      }
    | {
        id: string
      }
    | null,
): boolean {
  if (country1 === null && country2 === null) return false
  if ((country1 === null && country2 !== null) || (country1 !== null && country2 === null)) return true

  if (country1 && 'id' in country1 && country2 && 'id' in country2) return country1?.id !== country2?.id
  if (country1 && 'isoCode' in country1 && country2 && 'isoCode' in country2) return country1?.isoCode !== country2?.isoCode

  return true
}
